<template>
	<v-card color="rgba(41, 41, 47, 0.8)" class="dialog-card">
		<foundation-excavation-surface-editor v-if="readyToShow" :key="existingObj?.id ?? 'new'"
			class="foundation-excavation-surface-editor" :initialSnapshot.prop="initialSnapshot"
			:initialCoordinateSetting.prop="initialCoordinateSetting" :designDocumentAnalysisClient.prop="analysisClient"
			@foundation-excavation-surface-complete="onFoundationExcavationSurfaceComplete">
			<button slot="header-end" type="button" class="close-button" :aria-label="$t('CANCEL')" @click="endSession">
				×
			</button>
		</foundation-excavation-surface-editor>
	</v-card>
</template>

<script lang="ts">
import { FoundationExcavationObject } from "@/models/objects/FoundationExcavationObject";
import { ObjectType } from "@/models/objects/types";
import { InProgressMode } from "@/store/modules/inprogress-mode-manager";
import type {
	CoordinateSetting,
	ModelSnapshot,
} from "@earthbrain/foundation-excavation-sdk";
import type { FoundationExcavationSurfaceCompleteDetail } from "@earthbrain/foundation-excavation-sdk/components";
import { defineComponent } from "vue";
import { createDesignDocumentAnalysisClient } from "./analysisClient";

const DIALOG_NAME = "propertyDialogFoundationExcavation";

const pad2 = (n: number) => String(n).padStart(2, "0");
const formatDateName = (d: Date) =>
	`根伐り ${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

export default defineComponent({
	name: "PropertyDialogFoundationExcavation",
	computed: {
		existingObj(): FoundationExcavationObject | undefined {
			const obj = this.$store.state.object.objectUnderEdit;
			return obj?.type === ObjectType.FOUNDATION_EXCAVATION ? obj : undefined;
		},
		initialSnapshot(): ModelSnapshot | undefined {
			return this.existingObj?.snapshot;
		},
		initialCoordinateSetting(): CoordinateSetting | undefined {
			return this.existingObj?.coordinateSetting;
		},
		analysisClient():
			| ReturnType<typeof createDesignDocumentAnalysisClient>
			| undefined {
			const siteId = this.$store.state.site?.site_uuid as string | undefined;
			return siteId ? createDesignDocumentAnalysisClient(siteId) : undefined;
		},
		readyToShow(): boolean {
			return (
				this.$store.state.inProgressModeManager?.inProgressMode !==
					InProgressMode.EDIT || !!this.existingObj
			);
		},
	},
	beforeUnmount() {
		if (
			this.$store.state.inProgressModeManager?.objectType ===
			ObjectType.FOUNDATION_EXCAVATION
		) {
			this.$store.commit("inProgressModeManager/clearInProgressMode");
		}
	},
	methods: {
		async endSession(): Promise<void> {
			if (this.existingObj) {
				await this.$store.dispatch("inProgressModeManager/endEdit");
				return;
			}
			this.$store.commit("inProgressModeManager/clearInProgressMode");
			this.$store.commit("dialogs/closeDialog", DIALOG_NAME);
		},
		async onFoundationExcavationSurfaceComplete(
			e: CustomEvent<FoundationExcavationSurfaceCompleteDetail>,
		): Promise<void> {
			const snapshot = e.detail?.snapshot;
			if (!snapshot) {
				console.error(
					"foundation-excavation-surface-complete: snapshot is missing",
				);
				return;
			}
			const coordinateSetting = e.detail?.coordinateSetting;
			if (!coordinateSetting) {
				console.error(
					"foundation-excavation-surface-complete: coordinateSetting is missing",
				);
				return;
			}
			const site = this.$store.state.site;
			const siteId = site.site_uuid;

			const existing = this.existingObj;
			if (existing) {
				await existing.update({ snapshot, coordinateSetting });
				try {
					const props = await existing.toJSON();
					await this.$store.dispatch("object/updateObj", {
						props,
						objConnectionJSON: null,
					});
				} catch (err) {
					console.error("Failed to update foundation excavation:", err);
					return;
				}
			} else {
				const now = new Date();
				const obj = new FoundationExcavationObject({
					coordinateSetting,
					meta: {
						name: formatDateName(now),
						createdAt: now.toISOString(),
					},
					snapshot,
				});
				try {
					await this.$store.dispatch("object/createObj", { siteId, obj });
				} catch (err) {
					console.error("Failed to save foundation excavation:", err);
					return;
				}
			}
			await this.endSession();
		},
	},
});
</script>

<style scoped>
.dialog-card {
	height: 100%;
	display: flex;
	flex-direction: column;
}

.foundation-excavation-surface-editor {
	flex: 1 1 auto;
	min-height: 0;
	display: block;
}

.close-button {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	border: none;
	border-radius: 4px;
	background: transparent;
	color: #fff;
	font-size: 20px;
	line-height: 1;
	cursor: pointer;
}

.close-button:hover {
	background: rgba(255, 255, 255, 0.1);
}
</style>
